import {
  Component,
  forwardRef,
  inject,
  input,
  OnInit,
  ViewEncapsulation,
  DestroyRef,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NgControl,
  ReactiveFormsModule,
  TouchedChangeEvent,
  ValidationErrors
} from '@angular/forms';

import { TranslatePipe } from '@ngx-translate/core';

import { ColorPickerDirective } from 'ngx-color-picker';
import { filter, startWith } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';

import { AppFormFieldControl } from '../../form-field/form-field-control/form-field-control';
import { AutofocusDirective } from '../../../../../directives/autofocus/autofocus.directive';

@Component({
  selector: 'app-color-picker',
  templateUrl: './color-picker.component.html',
  styleUrls: ['./color-picker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: AppFormFieldControl,
      useExisting: forwardRef(() => ColorPickerComponent)
    }
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ColorPickerDirective,
    FormsModule,
    TranslatePipe,
    AutofocusDirective,
    ReactiveFormsModule
  ]
})
export class ColorPickerComponent implements OnInit, ControlValueAccessor {
  #destroyRef = inject(DestroyRef);
  public ngControl = inject(NgControl, { self: true, optional: true });

  readonly title = input<string>(undefined);
  readonly autoFocus = input(false);
  readonly description = input<string>(undefined);
  readonly required = input(true);
  readonly disabled = input(false);
  readonly showTextInput = input(true);

  value: unknown = null; // Holds the actual value of the select

  // Needed for AppFormFieldControl to display the error messages
  stateChanges: Subject<void> = new Subject<void>();
  errors: ValidationErrors | null = null;
  touched = false;

  constructor() {
    if (this.ngControl) {
      // Note: we provide the value accessor through here, instead of
      // the `providers` to avoid running into a circular import.
      // Ref: https://github.com/angular/components/blob/main/src/material/select/select.ts#L618
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit() {
    if (this.ngControl) {
      // Sync `errors` & `touched` state with the values needed for AppFormFieldControl
      this.ngControl.control.statusChanges
        ?.pipe(
          // Use startWith to set the values initially once
          startWith(null),
          takeUntilDestroyed(this.#destroyRef)
        )
        .subscribe(() => {
          this.errors = this.ngControl.errors;
          this.stateChanges.next();
        });
      this.ngControl.control.events
        .pipe(
          // Use startWith to set the values initially once
          startWith(null),
          filter(event => event instanceof TouchedChangeEvent),
          takeUntilDestroyed(this.#destroyRef)
        )
        .subscribe(() => {
          this.touched = this.ngControl.control?.touched ?? false;
          this.stateChanges.next();
        });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: unknown) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  writeValue(obj: unknown): void {
    this.value = obj; // Only update internal value, don’t trigger change
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  onValueChanges(v: unknown) {
    this.value = v;
    this.onChange(v); // Notify Angular forms system
    this.markAsTouched();
  }

  markAsTouched() {
    this.onTouched(); // Notify Angular that the field was touched
  }

  clearColor() {
    this.onValueChanges(null);
  }
}
